<template>
    <section ref="refProfiles" class="slice job-page__profiles">
        <div class="slice-content">
            <h2 class="slice-title joy-mb-12">{{ currentTitle }}</h2>
            <template v-if="currentProfiles?.length">
                <JobPageProfilesTabs v-if="tabs.length" v-model="currentTab" :tabs="tabs" />
                <JobPageProfilesList :profiles="currentProfiles" />
            </template>
            <div v-if="currentSearchUrl" class="text-center joy-mt-12">
                <VJoyButton
                    :href="$withLocalePrefix(currentSearchUrl)"
                    variant="main"
                    rel="noindex,nofollow"
                    data-component-source="category-landing-page"
                >
                    {{ ctaLabel }}
                </VJoyButton>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, inject, ref} from 'vue';
    import {navigateTo} from '@typed-router';
    import {useComponentTracking, useTranslation} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import type {LandingPageContentWithHighlightedProfiles} from '@api';
    import domTypeGuards from '~/composables/domTypeGuards';
    import {JOB_PAGE} from '~/keys';
    import JobPageProfilesList from './JobPageProfilesList.vue';
    import JobPageProfilesTabs from './JobPageProfilesTabs.vue';

    type JobPageTab = {name: 'experienced' | 'new' | 'available'; label: string};

    const {hasProperty} = domTypeGuards();
    const {t} = useTranslation();
    const refProfiles = ref<HTMLElement | null>(null);

    const {landingPage, prismicTemplate, jobPageConfig} = inject(JOB_PAGE) || {};
    const jobLabel = landingPage?.textualContent?.thisPage.jobLabel || '';

    const isHighlightedProfiles = (page: unknown): page is LandingPageContentWithHighlightedProfiles =>
        hasProperty(page, 'experiencedFreelancerProfiles');

    const currentTab = ref<JobPageTab['name']>('experienced');
    const tabs = ref<JobPageTab[]>([]);
    if (isHighlightedProfiles(landingPage)) {
        if (landingPage.experiencedFreelancerProfiles?.length) {
            tabs.value.push({name: 'experienced', label: t('landing.acquisition.profiles.tabs.experienced')});
        }
        if (landingPage.availableFreelancerProfiles?.length) {
            tabs.value.push({name: 'available', label: t('landing.acquisition.profiles.tabs.available')});
        }
        if (landingPage.newFreelancerProfiles?.length) {
            tabs.value.push({name: 'new', label: t('landing.acquisition.profiles.tabs.new')});
        }
    }
    // prevent single tab
    if (tabs.value.length < 2) {
        tabs.value = [];
    }

    const currentProfiles = computed(() => {
        const profiles = isHighlightedProfiles(landingPage) ? landingPage[`${currentTab.value}FreelancerProfiles`] : landingPage?.profiles;
        return profiles?.slice(0, 11); // makes room for help card
    });

    const currentSearchUrl = computed(() => {
        if (isHighlightedProfiles(landingPage)) {
            switch (currentTab.value) {
                case 'experienced':
                    return landingPage.searchMoreExperiencedProfilesUrl;
                case 'available':
                    return landingPage.searchMoreAvailableProfilesUrl;
            }
            return landingPage.seeMoreProfilesUrl;
        }
        return landingPage?.seeMoreProfilesUrl;
    });

    const currentTitle = computed(() => {
        const title = jobPageConfig?.primary.profilesTitle || prismicTemplate?.data.profilesTitle;
        return title?.replace('{0}', jobLabel);
    });

    const ctaLabel = t('landing.acquisition.profiles.view.all');

    useComponentTracking(refProfiles);
</script>
